/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { ReactComponent as DeleteIcon } from '@vkontakte/icons/src/svg/20/delete_clock_alt_outline_20.svg';
import { ReactComponent as DocumentIcon } from '@vkontakte/icons/src/svg/20/document_outline_20.svg';
import classNames from 'clsx';
import { ReactComponent as ProIcon } from 'img/pro-label.svg';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { abSharingLinkFakedoor, isAbNewSharingFolder } from 'reactApp/appHelpers/featuresHelpers';
import { isPublicPasswordEnabled } from 'reactApp/appHelpers/featuresHelpers/features/publicPassword';
import { isThemedSharedModalItem } from 'reactApp/appHelpers/featuresHelpers/features/themedPublic';
import { BaseConfirmDialog } from 'reactApp/components/BaseConfirmDialog/BaseConfirmDialog';
import { EButtonType } from 'reactApp/components/BaseConfirmDialog/BaseConformationDialog.types';
import { useConfirmDialogContent } from 'reactApp/components/SharingWindow/blocks/Weblink/AutoDelete/useConfirmDialogContent';
import { useShowTooltip } from 'reactApp/components/SharingWindow/blocks/Weblink/hooks/useShowTooltip';
import styles from 'reactApp/components/SharingWindow/blocks/Weblink/SharingWeblink.css';
import { AUTODELETE_OPTIONS, QA_VALUE } from 'reactApp/components/SharingWindow/Sharing.constants';
import { AutoDeletePeriod } from 'reactApp/components/SharingWindow/Sharing.types';
import { SharingDropdown } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown';
import { EDropdownColors } from 'reactApp/components/SharingWindow/ui/Dropdown/SharingDropdown.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFeatureAbAutoDelete } from 'reactApp/modules/features/features.selectors';
import { getItemTypeName } from 'reactApp/modules/file/utils';
import type { XrayItemType } from 'reactApp/modules/home/home.types';
import { updateWeblinkAutoDeleteRequest } from 'reactApp/modules/modifying/modifying.actions';
import type { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { useDisableSeoTooltip } from 'reactApp/ui/DisableSeoTooltip/hooks/useDisableSeoTooltip';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { PromoProTooltip } from 'reactApp/ui/PromoProTooltip/PromoProTooltip';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { formatAutoDeleteExpires } from 'reactApp/utils/formatDate';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { AutoDeleteOnboardingTooltip } from './tooltip/AutoDeleteOnboardingTooltip';

const defaultOption = AUTODELETE_OPTIONS[AutoDeletePeriod.None];
const proTooltipId = 'autodelete-pro-tooltip';

const CONFIRM_POPUP_LABEL = 'confirm-popup';

const BUTTONS = [EButtonType.accept, EButtonType.cancel];

interface IProps {
    item: PublishItem;
    disabled?: boolean;
    disabledBySeo?: boolean;
    gaSuffix?: string;
    onDropdownClick?: () => void;
    isOnboarding?: boolean;
    isOnboardingDesign?: boolean;
    isTabs?: boolean;
    xrayItemType: XrayItemType;
}

export const AutoDelete = memo(
    // eslint-disable-next-line complexity
    ({
        item,
        disabled,
        disabledBySeo,
        gaSuffix,
        onDropdownClick,
        isOnboarding = false,
        isOnboardingDesign = false,
        isTabs,
        xrayItemType,
    }: IProps): ReactElement | null => {
        const dispatch = useDispatch();
        const expires = 'weblinkExpires' in item && item.weblinkExpires;
        const isAutoDelete = 'weblinkAutoDelete' in item && item.weblinkAutoDelete;
        const folderName = item.name;
        const tariffsType = useSelector(getFeatureAbAutoDelete)?.data;
        const isPhone = useSelector(EnvironmentSelectors.isPhone);
        const proRef = useRef<HTMLDivElement>(null);

        const itemName = getItemTypeName(item);

        const { tooltip, handleShowTip } = useDisableSeoTooltip(proRef, itemName, disabledBySeo);

        // tempexp_17237-next-line
        const tooltipAutoshowDisabledByExp = abSharingLinkFakedoor === 'b';
        const {
            needShowTooltip: showProTooltip,
            closeTooltip: closeProTooltip,
            openTooltip: openProTooltip,
        } = useShowTooltip({
            onShow: () => {
                if (isOnboarding) {
                    emitAnalyticEvent(AnalyticEventNames.ONBOARDING_AUTODELETE_SHOW);
                    sendAutoDeleteGa('click', disabled ? 'disabled' : '');
                }
            },
            tooltipId: proTooltipId,
            preventShow: !disabled || isPhone,
            // tempexp_17341-next-line
            autoShow: disabled && !tooltipAutoshowDisabledByExp && !isThemedSharedModalItem && !isPublicPasswordEnabled,
        });

        /* tempexp_15752-next-line */
        const abSharing = isAbNewSharingFolder && !isPhone;

        const [showConfirmAutoDelete, setShowConfirmAutoDelete] = useState<boolean>(false);
        const [autoDeleteExpires, setAutoDeleteExpires] = useState<number>(0);

        const { renderDialogContent, renderDialogHeader } = useConfirmDialogContent({
            name: folderName,
            expires: autoDeleteExpires,
        });

        const sendAutoDeleteGa = useCallback(
            (action, label) => {
                if (isOnboarding) {
                    sendPaymentGa({ eventCategory: 'button-autodelete', action, label });
                } else {
                    sendPaymentGa({ eventCategory: 'auto-delete', action, label });
                }
                if (gaSuffix) {
                    sendPaymentGa({ eventCategory: `auto-delete-${gaSuffix}`, action, label });
                }
            },
            [gaSuffix, isOnboarding]
        );

        useEffect(() => {
            sendAutoDeleteGa('show', disabled ? 'disabled' : '');
            if (isOnboarding) {
                emitAnalyticEvent(AnalyticEventNames.ONBOARDING_AUTODELETE_SHOW);
            }
        }, []);

        const renderAutoDeleteDropdown = useCallback(
            (onClick): ReactElement => {
                const list = Object.values(AUTODELETE_OPTIONS).map((item) => ({
                    id: item.id,
                    text: item.text,
                    check: !isAutoDelete && item.id === AutoDeletePeriod.None,
                    qaValue: item.id,
                }));

                if (expires && isAutoDelete) {
                    list.unshift({
                        text: `Удалить ${formatAutoDeleteExpires(expires)}`,
                        id: AutoDeletePeriod.Autodeleting,
                        check: true,
                        qaValue: AutoDeletePeriod.Autodeleting,
                    });
                }

                return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
            },
            [expires, isAutoDelete]
        );

        const handleUpdateAutoDelete = useCallback(
            (autoDeletePeriod: AutoDeletePeriod): void => {
                sendAutoDeleteGa('dropdown-click', autoDeletePeriod);

                if (autoDeletePeriod === AutoDeletePeriod.Autodeleting) {
                    return;
                }

                sendPaymentGa({
                    eventCategory: ECategoryGa.public,
                    action: 'edit-autodelete',
                    time_autodelete: autoDeletePeriod,
                    public_id: item?.weblink,
                });

                if (autoDeletePeriod === AutoDeletePeriod.None) {
                    dispatch(updateWeblinkAutoDeleteRequest({ item, expires: 0, xrayItemType }));
                    return;
                }

                const expires = AUTODELETE_OPTIONS[autoDeletePeriod]?.getValue();
                setAutoDeleteExpires(expires);
                setShowConfirmAutoDelete(true);
                sendAutoDeleteGa('show', CONFIRM_POPUP_LABEL);
            },
            [dispatch, item, sendAutoDeleteGa, xrayItemType]
        );

        const handleCloseConfirmAutoDelete = useCallback(() => {
            sendAutoDeleteGa('close', CONFIRM_POPUP_LABEL);
            setShowConfirmAutoDelete(false);
            setAutoDeleteExpires(0);
        }, []);

        const updateAutoDeleteExpires = useCallback(() => {
            sendAutoDeleteGa('success', CONFIRM_POPUP_LABEL);
            dispatch(updateWeblinkAutoDeleteRequest({ item, expires: autoDeleteExpires, xrayItemType }));
            handleCloseConfirmAutoDelete();
        }, [autoDeleteExpires, dispatch, handleCloseConfirmAutoDelete, item, sendAutoDeleteGa, xrayItemType]);

        const dropdownValue = useMemo(() => {
            return expires && isAutoDelete ? `будут удалены ${formatAutoDeleteExpires(expires)}` : defaultOption?.text;
        }, [expires, isAutoDelete]);

        const tooltipNode = isOnboarding ? (
            <AutoDeleteOnboardingTooltip target={proRef} onClose={closeProTooltip} />
        ) : (
            <PromoProTooltip target={proRef} onClose={closeProTooltip} gaLabel="autodelete-tooltip" tariffsType={tariffsType} />
        );

        const mainText = isOnboardingDesign ? 'Автоудаление папки' : 'Файлы из папки';
        const deleteIcon = isOnboardingDesign ? <DeleteIcon /> : <DocumentIcon />;
        return (
            <div
                className={classNames(styles.rightItem, {
                    [styles.rightItem_disabled]: (disabled || disabledBySeo) && !isOnboardingDesign,
                })}
                data-qa-item={QA_VALUE.autoDeleteItem}
                onClick={disabledBySeo ? handleShowTip : openProTooltip}
            >
                <span className={classNames(styles.rightTitle, { [styles.tabs]: isTabs })}>
                    {isTabs && deleteIcon}
                    {mainText}
                </span>
                {/* tempexp_15752-next-line */}
                <div
                    className={classNames(styles.dropdown, { [styles.disabledOnboarding]: isOnboarding && (disabled || disabledBySeo) })}
                    ref={abSharing ? proRef : undefined}
                >
                    <Dropdown
                        withinDialog
                        theme="octavius"
                        qaValue={defaultOption.id}
                        as={SharingDropdown}
                        color={isAutoDelete ? EDropdownColors.RED : EDropdownColors.BLUE}
                        value={isPhone ? dropdownValue : dropdownValue.toLowerCase()}
                        content={renderAutoDeleteDropdown}
                        onClickItem={handleUpdateAutoDelete}
                        placement={tooltipPlacements.BOTTOM_LEFT}
                        qaValueContent={QA_VALUE.autoDeleteDropdownContent}
                        disabled={disabled || disabledBySeo}
                        animatedTooltip
                        tooltipClassName={isPhone ? styles.tooltip : undefined}
                        isPhone={isPhone}
                        isTabs={isTabs}
                        isOnboarding={isOnboarding}
                        onDropdownClick={onDropdownClick}
                        noPageOffset
                    />
                </div>
                {
                    /* tempexp_15752-start */
                    !abSharing && (
                        <div className={styles.rightIcon} ref={proRef} onMouseEnter={openProTooltip}>
                            {tariffsType === 'paid' ? (
                                <div className={styles.paidLabel}>По подписке</div>
                            ) : (
                                <ProIcon height={21} width={39} />
                            )}
                        </div>
                    )
                    /* tempexp_15752-end */
                }
                {showProTooltip && <Portal>{tooltipNode}</Portal>}
                {showConfirmAutoDelete && (
                    <BaseConfirmDialog
                        successButtonText="Включить автоудаление"
                        cancelButtonText="Не включать"
                        buttons={BUTTONS}
                        onClose={handleCloseConfirmAutoDelete}
                        onSuccess={updateAutoDeleteExpires}
                        renderContent={renderDialogContent}
                        renderHeader={renderDialogHeader}
                    />
                )}
                {tooltip}
            </div>
        );
    }
);

AutoDelete.displayName = 'AutoDelete';
